"use client";

import { useState, useRef, useEffect } from "react";
import { signOut } from "next-auth/react";
import { getInitials } from "@/lib/utils";
import { getRoleLabel } from "@/lib/rbac";
import { Badge } from "@/components/ui/Badge";
import type { UserRole } from "@prisma/client";

interface UserMenuProps {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    role: UserRole;
  };
}

export function UserMenu({ user }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 min-h-[48px] hover:bg-white/5 transition-colors"
      >
        {user.image ? (
          <img src={user.image} alt="" className="h-7 w-7 rounded-full" />
        ) : (
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-border-2 text-xs font-medium text-white">
            {getInitials(user.name || user.email || "U")}
          </div>
        )}
        <span className="hidden text-sm text-white/70 md:block">{user.name || user.email}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-xl border border-surface-border bg-surface shadow-xl">
          <div className="border-b border-surface-border px-4 py-3">
            <p className="text-sm font-medium text-white">{user.name}</p>
            <p className="text-xs text-muted">{user.email}</p>
            <Badge variant="info" size="sm" className="mt-1.5">
              {getRoleLabel(user.role)}
            </Badge>
          </div>
          <div className="p-1">
            <button
              onClick={() => signOut({ callbackUrl: "/login" })}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 min-h-[44px] text-sm text-muted hover:bg-white/5 hover:text-white transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
